import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, TextField,
  IconButton, Tooltip, LinearProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import {
  ShoppingCart as POIcon, Visibility, LocalShipping, CheckCircle,
} from '@mui/icons-material';

const API = '/api/wmc';

const statusColors: Record<string, string> = {
  draft: '#6B7280',
  submitted: '#3B82F6',
  acknowledged: '#8B5CF6',
  partial: '#F59E0B',
  received: '#10B981',
  closed: '#374151',
  cancelled: '#EF4444',
};

interface PurchaseOrder {
  id: string;
  po_number: string;
  supplier_name: string;
  status: string;
  ordered_date: string | null;
  expected_delivery_date: string | null;
  actual_delivery_date: string | null;
  shipping_method: string | null;
  tracking_number: string | null;
  total_value: string | null;
  currency: string;
  line_count: number;
  notes: string | null;
}

interface PODetail extends PurchaseOrder {
  lines: Array<{
    id: string;
    line_number: number;
    material_name: string | null;
    material_code: string | null;
    quantity_ordered: string;
    quantity_received: string;
    unit: string;
    unit_cost: string | null;
  }>;
}

const WmcPurchaseOrders: React.FC = () => {
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [detail, setDetail] = useState<PODetail | null>(null);

  useEffect(() => {
    loadOrders();
  }, [statusFilter]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const res = await fetch(`${API}/purchase-orders/${params}`);
      const data = await res.json();
      setOrders(data.results || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const openDetail = async (id: string) => {
    try {
      const res = await fetch(`${API}/purchase-orders/${id}/`);
      const data = await res.json();
      setDetail(data);
    } catch (e) { console.error(e); }
  };

  const filtered = orders.filter(o =>
    !search ||
    o.po_number.toLowerCase().includes(search.toLowerCase()) ||
    o.supplier_name.toLowerCase().includes(search.toLowerCase())
  );

  const isLate = (po: PurchaseOrder) => {
    if (!po.expected_delivery_date || po.status === 'received' || po.status === 'closed' || po.status === 'cancelled') return false;
    return new Date(po.expected_delivery_date) < new Date();
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <POIcon /> Purchase Orders
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Open', value: orders.filter(o => ['draft', 'submitted', 'acknowledged', 'partial'].includes(o.status)).length, color: '#3B82F6' },
          { label: 'Received', value: orders.filter(o => o.status === 'received').length, color: '#10B981' },
          { label: 'Late', value: orders.filter(isLate).length, color: '#EF4444' },
          { label: 'Total Value', value: `$${orders.reduce((s, o) => s + parseFloat(o.total_value || '0'), 0).toLocaleString()}`, color: '#F59E0B' },
        ].map(card => (
          <Grid item xs={3} key={card.label}>
            <Card sx={{ borderLeft: `4px solid ${card.color}` }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                <Typography variant="h5" sx={{ color: card.color }}>{card.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search POs..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ width: 300 }}
        />
        <FormControl size="small" sx={{ width: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} label="Status">
            <MenuItem value="">All</MenuItem>
            {Object.keys(statusColors).map(s => (
              <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer component={Paper} sx={{ backgroundColor: '#1F2937' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 'bold', color: '#9CA3AF', borderBottom: '2px solid #374151' } }}>
              <TableCell>PO #</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Ordered</TableCell>
              <TableCell>Expected</TableCell>
              <TableCell>Delivered</TableCell>
              <TableCell>Lines</TableCell>
              <TableCell align="right">Value</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(po => (
              <TableRow key={po.id} hover sx={{
                backgroundColor: isLate(po) ? 'rgba(239,68,68,0.05)' : undefined,
              }}>
                <TableCell><Typography variant="body2" fontWeight="bold">{po.po_number}</Typography></TableCell>
                <TableCell>{po.supplier_name}</TableCell>
                <TableCell>
                  <Chip
                    label={po.status}
                    size="small"
                    sx={{ backgroundColor: `${statusColors[po.status]}20`, color: statusColors[po.status], fontWeight: 'bold', textTransform: 'capitalize' }}
                  />
                </TableCell>
                <TableCell>{po.ordered_date || '—'}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ color: isLate(po) ? '#EF4444' : '#D1D5DB', fontWeight: isLate(po) ? 'bold' : 'normal' }}>
                    {po.expected_delivery_date || '—'}
                    {isLate(po) && ' ⚠'}
                  </Typography>
                </TableCell>
                <TableCell>{po.actual_delivery_date || '—'}</TableCell>
                <TableCell align="center">{po.line_count}</TableCell>
                <TableCell align="right">{po.total_value ? `$${parseFloat(po.total_value).toLocaleString()}` : '—'}</TableCell>
                <TableCell>
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => openDetail(po.id)}>
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detail Dialog */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#1F2937' }}>
          Purchase Order — {detail?.po_number}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#1F2937' }}>
          {detail && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Supplier</Typography>
                  <Typography>{detail.supplier_name}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Chip label={detail.status} size="small" sx={{ textTransform: 'capitalize' }} />
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Total Value</Typography>
                  <Typography>{detail.total_value ? `$${parseFloat(detail.total_value).toLocaleString()} ${detail.currency}` : '—'}</Typography>
                </Grid>
                {detail.tracking_number && (
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Tracking</Typography>
                    <Typography>{detail.tracking_number}</Typography>
                  </Grid>
                )}
                {detail.notes && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Notes</Typography>
                    <Typography variant="body2">{detail.notes}</Typography>
                  </Grid>
                )}
              </Grid>

              <Typography variant="h6" sx={{ mb: 1 }}>Line Items</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Material</TableCell>
                    <TableCell align="right">Ordered</TableCell>
                    <TableCell align="right">Received</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell align="right">Unit Cost</TableCell>
                    <TableCell align="right">Line Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detail.lines.map(line => {
                    const ordered = parseFloat(line.quantity_ordered);
                    const received = parseFloat(line.quantity_received);
                    const pct = ordered > 0 ? (received / ordered) * 100 : 0;
                    return (
                      <TableRow key={line.id}>
                        <TableCell>{line.line_number}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{line.material_name}</Typography>
                          <Typography variant="caption" color="text.secondary">{line.material_code}</Typography>
                        </TableCell>
                        <TableCell align="right">{ordered} {line.unit}</TableCell>
                        <TableCell align="right">{received} {line.unit}</TableCell>
                        <TableCell sx={{ width: 120 }}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(pct, 100)}
                            sx={{
                              height: 8, borderRadius: 4,
                              backgroundColor: '#374151',
                              '& .MuiLinearProgress-bar': { backgroundColor: pct >= 100 ? '#10B981' : '#3B82F6' }
                            }}
                          />
                          <Typography variant="caption" color="text.secondary">{pct.toFixed(0)}%</Typography>
                        </TableCell>
                        <TableCell align="right">{line.unit_cost ? `$${line.unit_cost}` : '—'}</TableCell>
                        <TableCell align="right">
                          {line.unit_cost ? `$${(ordered * parseFloat(line.unit_cost)).toFixed(2)}` : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#1F2937' }}>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WmcPurchaseOrders;
